// Agent config-version API: list, getActive, getDraft, publish (with
// optional activate=false to save a draft), activate, delete.
// See react-rewrite-plan.md §11.3 (api-entities-settings).

import type { ApiClient } from '@/shared/api';
import { ApiError } from '@/shared/api';
import {
  toAgentConfigVersion,
  toAgentConfigVersionList,
} from '../model/toAgentConfigView';
import type { AgentConfigVersionView } from '../model/types';

export interface PublishConfigInput {
  payload: Record<string, unknown>;
  changeSummary?: string;
  activate?: boolean;
}

export interface AgentConfigApi {
  listVersions(agentId: string, signal?: AbortSignal): Promise<AgentConfigVersionView[]>;
  getActive(agentId: string, signal?: AbortSignal): Promise<AgentConfigVersionView | null>;
  getDraft(agentId: string, signal?: AbortSignal): Promise<AgentConfigVersionView | null>;
  publish(
    agentId: string,
    input: PublishConfigInput,
    signal?: AbortSignal,
  ): Promise<AgentConfigVersionView>;
  activate(
    agentId: string,
    versionId: string,
    signal?: AbortSignal,
  ): Promise<AgentConfigVersionView>;
  deleteVersion(agentId: string, versionId: string, signal?: AbortSignal): Promise<void>;
}

export class AgentConfigMutationError extends ApiError {
  constructor(message: string) {
    super({
      userMessage: 'The agent configuration change could not be completed. Please try again.',
      devMessage: message,
    });
    this.name = 'AgentConfigMutationError';
  }
}

function configRoot(agentId: string): string {
  return `/api/v1/owner/agents/${encodeURIComponent(agentId)}/config`;
}

export function createAgentConfigApi(client: ApiClient): AgentConfigApi {
  return {
    async listVersions(agentId, signal) {
      const raw = await client.request({
        path: `${configRoot(agentId)}/versions`,
        signal,
      });
      return toAgentConfigVersionList(raw);
    },

    async getActive(agentId, signal) {
      try {
        const raw = await client.request({
          path: `${configRoot(agentId)}/active`,
          signal,
        });
        return toAgentConfigVersion(raw);
      } catch {
        // Backend returns 404 when no active version exists yet — treat as null.
        return null;
      }
    },

    async getDraft(agentId, signal) {
      try {
        const raw = await client.request({
          path: `${configRoot(agentId)}/draft`,
          signal,
        });
        return toAgentConfigVersion(raw);
      } catch (error) {
        if (error instanceof ApiError && error.context?.status === 404) return null;
        throw error;
      }
    },

    async publish(agentId, input, signal) {
      const activate = input.activate ?? true;
      const raw = await client.request({
        method: 'POST',
        path: `${configRoot(agentId)}/versions`,
        query: activate ? undefined : { activate: false },
        body: {
          payload: input.payload,
          change_summary: input.changeSummary ?? null,
        },
        signal,
      });
      const view = toAgentConfigVersion(raw);
      if (view === null) {
        throw new AgentConfigMutationError('publish returned an unexpected payload');
      }
      return view;
    },

    async activate(agentId, versionId, signal) {
      const raw = await client.request({
        method: 'POST',
        path: `${configRoot(agentId)}/versions/${encodeURIComponent(versionId)}/activate`,
        signal,
      });
      const view = toAgentConfigVersion(raw);
      if (view === null) {
        throw new AgentConfigMutationError('activate returned an unexpected payload');
      }
      return view;
    },

    async deleteVersion(agentId, versionId, signal) {
      await client.request({
        method: 'DELETE',
        path: `${configRoot(agentId)}/versions/${encodeURIComponent(versionId)}`,
        signal,
      });
    },
  };
}
